import type { AuthUser, Tokens } from './authApi'

/** The signed-in session as this browser keeps it, under one localStorage key shared by its tabs. */
export interface Session {
  accessToken: string
  /** Epoch milliseconds, computed from the server's `expiresIn` when the token arrived. */
  accessTokenExpiresAt: number
  refreshToken: string
  user: AuthUser
}

export const SESSION_KEY = 'macrotide.auth.session'

/** The part of the Web Storage API the session store uses. */
export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** window.localStorage, or null where there is none or it is blocked (even reading the property can throw). */
export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function sessionFromTokens(tokens: Tokens, now: number): Session {
  return {
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: now + tokens.expiresIn * 1000,
    refreshToken: tokens.refreshToken,
    user: { id: tokens.user.id, email: tokens.user.email },
  }
}

/** Parses a stored session; anything missing, malformed or not JSON reads as no session. */
export function parseSession(text: string | null): Session | null {
  if (!text) return null
  let value: unknown
  try {
    value = JSON.parse(text)
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) return null
  const record = value as Record<string, unknown>
  const user = record.user
  if (
    typeof record.accessToken !== 'string' ||
    record.accessToken === '' ||
    typeof record.accessTokenExpiresAt !== 'number' ||
    !Number.isFinite(record.accessTokenExpiresAt) ||
    typeof record.refreshToken !== 'string' ||
    record.refreshToken === '' ||
    typeof user !== 'object' ||
    user === null
  ) {
    return null
  }
  const { id, email } = user as Record<string, unknown>
  if (typeof id !== 'string' || id === '' || typeof email !== 'string' || email === '') return null
  return {
    accessToken: record.accessToken,
    accessTokenExpiresAt: record.accessTokenExpiresAt,
    refreshToken: record.refreshToken,
    user: { id, email },
  }
}

export function loadSession(storage: KeyValueStore | null): Session | null {
  if (!storage) return null
  try {
    return parseSession(storage.getItem(SESSION_KEY))
  } catch {
    return null
  }
}

export function saveSession(storage: KeyValueStore | null, session: Session): void {
  if (!storage) return
  try {
    storage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // Full or blocked storage: the session still works in this tab, it just isn't remembered.
  }
}

export function clearSession(storage: KeyValueStore | null): void {
  if (!storage) return
  try {
    storage.removeItem(SESSION_KEY)
  } catch {
    // Blocked storage held nothing to clear.
  }
}

/** Removes what oidc-client-ts kept for the old Cognito sign-in (keys starting "oidc."). */
export function removeLegacyAuthState(storage: Storage | null): void {
  if (!storage) return
  try {
    const legacy: string[] = []
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index)
      if (key?.startsWith('oidc.')) legacy.push(key)
    }
    for (const key of legacy) storage.removeItem(key)
  } catch {
    // Blocked storage held nothing to remove.
  }
}
